import { TrendingDown } from 'lucide-react'
import { aiKeys, useOrderCheck, useOrderRisk } from '@/lib/aiInsights'
import { formatDate } from '@/lib/date'
import { cn } from '@/lib/cn'
import InsightCard from './InsightCard'
import { RiskBadge } from './AiBadges'

/**
 * MPG AI on the order page: the late-risk flag at once (real section speeds,
 * no model), and the AI check on request: verdict, section due dates, the
 * best production day and what to do, with any suggested move as an Apply card.
 */
export default function OrderAiCard({ orderId, orderNumber }: { orderId: number; orderNumber: string }) {
  const check = useOrderCheck(orderId)
  const risk = useOrderRisk(orderId)

  return (
    <InsightCard
      title="MPG AI check"
      insight={check.data}
      loading={check.isLoading}
      queryKey={aiKeys.check(orderId)}
      runPath={`/admin/ai/orders/${orderId}/check`}
      runLabel="Check with MPG AI"
      rerunLabel="Check again"
      emptyText="Ask MPG AI whether this order is on track, when each section is due and the best production day. It runs the real planner, so the dates match what the system would book."
      followUp={`About order ${orderNumber}: `}
    >
      {risk.data && (
        <div
          className={cn(
            'mt-3 flex items-start gap-2 rounded-lg border p-3 text-sm',
            risk.data.level === 'tight' ? 'border-warning/30 bg-warning/5' : 'border-danger/30 bg-danger/5',
          )}
        >
          <TrendingDown className={cn('mt-0.5 size-4 shrink-0', risk.data.level === 'tight' ? 'text-warning' : 'text-danger')} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <RiskBadge risk={risk.data} />
              <span className="text-xs text-foreground/60">Delivery due {formatDate(risk.data.delivery_due)}</span>
            </div>
            <p className="mt-1 text-foreground/80">{risk.data.reason}</p>
          </div>
        </div>
      )}
    </InsightCard>
  )
}
